/**
 * <Tabs> — Radix Tabs primitive with primary-underline active indicator.
 *
 * Wraps `@radix-ui/react-tabs`. UI-SPEC §Color rule 3: active tab indicator
 * uses primary underline. Implementation:
 *   - Trigger has `border-b-2 border-transparent` by default.
 *   - Active state: `data-[state=active]:border-primary data-[state=active]:text-default`.
 *   - `border-b-*` is direction-agnostic — the underline stays under the active
 *     tab in both LTR and RTL without extra work.
 *
 * `Tabs.Trigger count` adds a small count badge at the trigger's inline end.
 * `Tabs.List overflow` (opt-in) moves the triggers that do not fit into a
 * "More (n)" popover button at the list's end. That button is not a tab (it
 * stays out of the tabs' roving focus), the active trigger never moves, and
 * the list measures itself, invisibly, whenever its width changes.
 *
 * Export surface: dotted composition `Tabs.Root/List/Trigger/Content` plus flat
 * exports for consumers that prefer destructured imports.
 */
import * as React from "react";
import * as RadixTabs from "@radix-ui/react-tabs";
import { useTranslation } from "react-i18next";
import { ChevronDown } from "lucide-react";
import { cn, formatCount } from "../../utils";
import { Badge } from "../Badge";
import { Icon } from "../Icon";
import { PopoverContent, PopoverRoot, PopoverTrigger } from "../Popover";
import { partitionTabs } from "./tabsOverflow";

// Only `i18n.language` and one label are read: a loading namespace must not suspend the tabs.
const I18N_OPTIONS = { useSuspense: false } as const;

/** The current tab and a way to choose one, for the list's "More" menu. */
const TabsSelectContext = React.createContext<{
  value: string | undefined;
  select: (value: string) => void;
} | null>(null);

/** The trigger values an overflowing list has moved into "More" (null: no overflow list). */
const TabsOverflowContext = React.createContext<ReadonlySet<string> | null>(null);

export const TabsRoot = React.forwardRef<
  React.ElementRef<typeof RadixTabs.Root>,
  React.ComponentPropsWithoutRef<typeof RadixTabs.Root>
>(function TabsRoot({ className, value, defaultValue, onValueChange, ...rest }, ref) {
  // Radix's own controlled/uncontrolled rule, held here so the "More" menu can choose a tab.
  const [inner, setInner] = React.useState(defaultValue);
  const current = value ?? inner;
  const select = React.useCallback(
    (next: string) => {
      if (value === undefined) setInner(next);
      onValueChange?.(next);
    },
    [value, onValueChange],
  );
  const context = React.useMemo(() => ({ value: current, select }), [current, select]);
  return (
    <TabsSelectContext.Provider value={context}>
      <RadixTabs.Root
        ref={ref}
        className={cn("flex flex-col gap-md", className)}
        {...(current !== undefined ? { value: current } : {})}
        onValueChange={select}
        {...rest}
      />
    </TabsSelectContext.Provider>
  );
});

export interface TabsListProps extends React.ComponentPropsWithoutRef<typeof RadixTabs.List> {
  /** Move triggers that do not fit into a "More (n)" menu instead of scrolling the list. */
  overflow?: boolean;
}

export const TabsList = React.forwardRef<React.ElementRef<typeof RadixTabs.List>, TabsListProps>(
  function TabsList({ className, overflow = false, children, ...rest }, ref) {
    const listClass = cn(
      // Logical flexbox — `gap-md` separates triggers; bottom border runs the
      // full width so inactive tabs read as muted tabs beneath a shared rule.
      "inline-flex max-w-full items-stretch gap-md overflow-x-auto border-b border-subtle [scrollbar-width:none]",
      className,
    );
    if (!overflow) {
      return (
        <RadixTabs.List ref={ref} className={listClass} {...rest}>
          {children}
        </RadixTabs.List>
      );
    }
    return (
      <OverflowList ref={ref} className={className} {...rest}>
        {children}
      </OverflowList>
    );
  },
);

/** One trigger as the "More" menu lists it. */
interface OverflowItem {
  value: string;
  label: string;
  count: string | undefined;
}

const OverflowList = React.forwardRef<
  React.ElementRef<typeof RadixTabs.List>,
  Omit<TabsListProps, "overflow">
>(function OverflowList({ className, children, ...rest }, forwardedRef) {
  const { t } = useTranslation("common", I18N_OPTIONS);
  const selectContext = React.useContext(TabsSelectContext);
  const listRef = React.useRef<HTMLDivElement | null>(null);
  const moreRef = React.useRef<HTMLButtonElement | null>(null);
  const [hidden, setHidden] = React.useState<readonly OverflowItem[]>([]);
  const [measuring, setMeasuring] = React.useState(true);
  const [open, setOpen] = React.useState(false);
  const hiddenValues = React.useMemo(() => new Set(hidden.map((item) => item.value)), [hidden]);

  const setRefs = React.useCallback(
    (node: HTMLDivElement | null) => {
      listRef.current = node;
      if (typeof forwardedRef === "function") forwardedRef(node);
      else if (forwardedRef !== null) forwardedRef.current = node;
    },
    [forwardedRef],
  );

  // Re-measure when the active tab or the set of triggers changes.
  const childCount = React.Children.count(children);
  React.useLayoutEffect(() => {
    setMeasuring(true);
  }, [selectContext?.value, childCount]);

  // Width changes re-measure on the next frame (one measure per frame at most).
  React.useEffect(() => {
    const list = listRef.current;
    if (list === null || typeof ResizeObserver === "undefined") return undefined;
    let frame = 0;
    let lastWidth = list.clientWidth;
    const observer = new ResizeObserver(() => {
      if (list.clientWidth === lastWidth) return;
      lastWidth = list.clientWidth;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setMeasuring(true));
    });
    observer.observe(list);
    // Web fonts change trigger widths without resizing the list.
    void document.fonts?.ready.then(() => setMeasuring(true));
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  // While measuring every trigger is laid out (the list is invisible), then the partition applies.
  React.useLayoutEffect(() => {
    if (!measuring) return;
    const list = listRef.current;
    if (list === null) return;
    const triggers = Array.from(list.querySelectorAll<HTMLElement>('[role="tab"]'));
    const gap = Number.parseFloat(getComputedStyle(list).columnGap) || 0;
    const { overflow } = partitionTabs({
      widths: triggers.map((trigger) => trigger.offsetWidth),
      available: list.clientWidth,
      gap,
      moreWidth: moreRef.current?.offsetWidth ?? 0,
      activeIndex: triggers.findIndex((trigger) => trigger.dataset.state === "active"),
    });
    setHidden(
      overflow.flatMap((index) => {
        const trigger = triggers[index];
        if (trigger === undefined) return [];
        return [
          {
            value: trigger.dataset.value ?? "",
            label: trigger.querySelector("[data-tab-label]")?.textContent ?? "",
            count: trigger.querySelector("[data-tab-count]")?.textContent ?? undefined,
          },
        ];
      }),
    );
    setMeasuring(false);
  }, [measuring]);

  return (
    <TabsOverflowContext.Provider value={measuring ? EMPTY : hiddenValues}>
      <RadixTabs.List
        ref={setRefs}
        className={cn(
          "flex w-full items-stretch gap-md overflow-hidden border-b border-subtle",
          className,
        )}
        style={measuring ? { visibility: "hidden" } : undefined}
        {...rest}
      >
        {children}
        {measuring || hidden.length > 0 ? (
          <PopoverRoot open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <button
                ref={moreRef}
                type="button"
                className={cn(
                  "ms-auto inline-flex shrink-0 items-center gap-xs whitespace-nowrap px-xs pb-sm pt-xs",
                  "border-b-2 border-transparent text-body text-muted hover:text-default",
                  "focus-visible:focus-ring focus-visible:outline-offset-[-2px]!",
                )}
              >
                {t("tabs.more", { count: hidden.length })}
                <Icon as={ChevronDown} size={14} />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-auto min-w-48 p-xs">
              <ul className="flex flex-col">
                {hidden.map((item) => (
                  <li key={item.value}>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between gap-sm rounded-sm px-sm py-xs text-start text-body hover:bg-surface-sunken focus-visible:focus-ring"
                      onClick={() => {
                        setOpen(false);
                        selectContext?.select(item.value);
                      }}
                    >
                      {item.label}
                      {item.count !== undefined ? <Badge size="sm">{item.count}</Badge> : null}
                    </button>
                  </li>
                ))}
              </ul>
            </PopoverContent>
          </PopoverRoot>
        ) : null}
      </RadixTabs.List>
    </TabsOverflowContext.Provider>
  );
});

const EMPTY: ReadonlySet<string> = new Set();

export interface TabsTriggerProps extends React.ComponentPropsWithoutRef<typeof RadixTabs.Trigger> {
  /** A small count badge at the trigger's inline end (a string is shown as given). */
  count?: number | string;
}

export const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof RadixTabs.Trigger>,
  TabsTriggerProps
>(function TabsTrigger({ className, count, children, value, ...rest }, ref) {
  const { i18n } = useTranslation("common", I18N_OPTIONS);
  const overflowed = React.useContext(TabsOverflowContext);
  const countBadge =
    count !== undefined ? (
      <Badge size="sm" {...(overflowed !== null ? { "data-tab-count": "" } : {})}>
        {formatCount(count, i18n.language)}
      </Badge>
    ) : null;
  return (
    <RadixTabs.Trigger
      ref={ref}
      value={value}
      className={cn(
        // Shape + typography — pb matches the list border-b so the underline
        // aligns with the list rule.
        "inline-flex items-center gap-sm px-xs pb-sm pt-xs shrink-0 whitespace-nowrap",
        "text-body",
        // Inactive state — muted text
        "text-muted",
        // Underline indicator — transparent by default; primary in active state.
        // `border-b-2` is direction-agnostic (bottom edge never flips).
        "border-b-2 border-transparent",
        // Active state — primary underline (UI-SPEC §Color rule 3) + default text
        "data-[state=active]:border-primary data-[state=active]:text-default",
        // Focus + disabled. The ring is drawn inside the trigger: the list scrolls
        // sideways (overflow-x-auto), which would clip an outline drawn outside it.
        "focus-visible:focus-ring focus-visible:outline-offset-[-2px]!",
        "disabled:opacity-50 disabled:pointer-events-none",
        "transition-colors duration-fast ease-out",
        // Moved into the list's "More" menu.
        overflowed?.has(value) === true && "hidden",
        className,
      )}
      {...(overflowed !== null ? { "data-value": value } : {})}
      {...rest}
    >
      {overflowed !== null ? <span data-tab-label="">{children}</span> : children}
      {countBadge}
    </RadixTabs.Trigger>
  );
});

export const TabsContent = React.forwardRef<
  React.ElementRef<typeof RadixTabs.Content>,
  React.ComponentPropsWithoutRef<typeof RadixTabs.Content>
>(function TabsContent({ className, ...rest }, ref) {
  return (
    <RadixTabs.Content
      ref={ref}
      className={cn("focus-visible:focus-ring rounded-sm outline-none", className)}
      {...rest}
    />
  );
});

/**
 * Dotted composition — matches D-05 hybrid pattern.
 * Consumers can use either flat (`<TabsRoot>`) or dotted (`<Tabs.Root>`).
 */
export const Tabs = {
  Root: TabsRoot,
  List: TabsList,
  Trigger: TabsTrigger,
  Content: TabsContent,
};
